import { useEffect, useState, useCallback } from 'react'
import { AlertCircle } from 'lucide-react'
import { valueDesignApi, personaWorkshopApi } from '@/lib/api/client'
import { ValueDesignCanvas } from './ValueDesignCanvas'
import { ValueDesignList } from './ValueDesignList'
import { Stepper, PersonaStep, JtbdStep } from './WizardSteps'
import type { PersonaWorkshopSession, PersonaData, JtbdData, ValuePropositionCanvas } from '@ispot/shared'
import { migratePersonaData } from '@ispot/shared'

interface Props {
  accountId: string
  projectId: string
}

type WizardStep = 'list' | 'select-persona' | 'select-jtbd' | 'canvas'

export function ValueDesignPage({ accountId, projectId }: Props) {
  const [step, setStep] = useState<WizardStep>('list')
  const [sessions, setSessions] = useState<PersonaWorkshopSession[]>([])
  const [vpcs, setVpcs] = useState<ValuePropositionCanvas[]>([])
  const [selectedSession, setSelectedSession] = useState<PersonaWorkshopSession | null>(null)
  const [selectedJtbd, setSelectedJtbd] = useState<JtbdData | null>(null)
  const [activeVpc, setActiveVpc] = useState<ValuePropositionCanvas | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadData = useCallback(async () => {
    setIsLoading(true)
    try {
      const [sessionsData, vpcsData] = await Promise.all([
        personaWorkshopApi.listSessions(accountId, projectId),
        valueDesignApi.list(accountId, projectId),
      ])
      setSessions(Array.isArray(sessionsData) ? sessionsData : [])
      setVpcs(Array.isArray(vpcsData) ? vpcsData : [])
    } catch {
      setSessions([])
      setVpcs([])
    } finally {
      setIsLoading(false)
    }
  }, [accountId, projectId])

  useEffect(() => { loadData() }, [loadData])

  const handleCreateVpc = async () => {
    if (!selectedSession || !selectedJtbd) return
    setError(null)
    try {
      const vpc = await valueDesignApi.create(accountId, projectId, {
        personaSessionId: selectedSession.id,
        jtbdId: selectedJtbd.id,
      })
      setActiveVpc(vpc)
      setStep('canvas')
      await loadData()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create VPC')
    }
  }

  const handleOpenVpc = async (vpc: ValuePropositionCanvas) => {
    const session = sessions.find(s => s.id === vpc.personaSessionId)
    if (session) {
      const pd = migratePersonaData(session.personaData as PersonaData)
      setSelectedSession(session)
      setSelectedJtbd(pd.jtbds?.find(j => j.id === vpc.jtbdId) || null)
    }
    setActiveVpc(vpc)
    setStep('canvas')
  }

  const handleBack = () => {
    if (step === 'select-jtbd') { setStep('select-persona'); setSelectedSession(null) }
    else if (step === 'canvas') { setStep('list'); setActiveVpc(null) }
    else if (step === 'select-persona') { setStep('list') }
  }

  if (isLoading) {
    return <div className="flex items-center justify-center h-64 text-[var(--color-text-muted)]">Loading...</div>
  }

  if (step === 'canvas' && activeVpc) {
    return (
      <ValueDesignCanvas
        accountId={accountId} projectId={projectId} vpc={activeVpc} session={selectedSession} jtbd={selectedJtbd}
        onBack={handleBack} onUpdate={async (updated) => { setActiveVpc(updated); await loadData() }}
      />
    )
  }

  if (step === 'list') {
    return (
      <ValueDesignList
        vpcs={vpcs} sessions={sessions} onOpen={handleOpenVpc} onNew={() => setStep('select-persona')}
        onDelete={async (id) => { await valueDesignApi.delete(accountId, projectId, id); await loadData() }}
      />
    )
  }

  return (
    <div className="max-w-3xl mx-auto py-8 px-4">
      <Stepper currentStep={step as 'select-persona' | 'select-jtbd'} />
      <div className="h-px bg-[var(--color-border)] mb-6" />

      {error && (
        <div className="flex items-center gap-2 p-3 mb-4 rounded-lg bg-red-50 text-red-700 text-sm">
          <AlertCircle className="w-4 h-4 shrink-0" />{error}
        </div>
      )}

      {step === 'select-persona' && (
        <PersonaStep
          sessions={sessions} selectedSession={selectedSession}
          onSelect={setSelectedSession} onBack={handleBack}
          onNext={() => { if (selectedSession) setStep('select-jtbd') }}
        />
      )}

      {step === 'select-jtbd' && selectedSession && (
        <JtbdStep
          session={selectedSession} vpcs={vpcs} selectedJtbd={selectedJtbd}
          onSelect={setSelectedJtbd}
          onBack={() => { setStep('select-persona'); setSelectedJtbd(null) }}
          onCreate={handleCreateVpc}
        />
      )}
    </div>
  )
}
